import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { formatApiErrorDetail } from '../auth/AuthContext';
import { formatNzDate, formatNzDateTime } from '../utils/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISCOUNT_FIELDS = [
  { key: 'small_single_pct', label: '1kg Single' },
  { key: 'small_box_pct',    label: '1kg Box' },
  { key: 'small_pallet_pct', label: '1kg Pallet' },
  { key: 'large_single_pct', label: '2kg Single' },
  { key: 'large_box_pct',    label: '2kg Box' },
  { key: 'large_pallet_pct', label: '2kg Pallet' },
];

// Modal for editing a customer's per-account overrides (discounts + product visibility).
// `user` is the customer object from the UsersPage table. `onClose(saved)` closes the modal.
export const CustomerOverridesModal = ({ user, onClose }) => {
  const [products, setProducts] = useState([]);
  const [overrides, setOverrides] = useState({});       // { key: '' | number } — empty = no override
  const [productDiscounts, setProductDiscounts] = useState({}); // productId -> '' | number (percent off RRP)
  const [hidden, setHidden] = useState(new Set());
  const [smartSaverEnabled, setSmartSaverEnabled] = useState(true);
  const [singleTubEnabled, setSingleTubEnabled] = useState(false);
  const [customerCode, setCustomerCode] = useState('');
  // Per-customer order-submission preferences (Feb 22 2026 — moved from global settings).
  const [bccCustomer, setBccCustomer] = useState(false);
  const [prepayEnabled, setPrepayEnabled] = useState(true);
  const [prepayLabel, setPrepayLabel] = useState('');
  const [prepayLabelTrail, setPrepayLabelTrail] = useState({ at: null, by: null });
  const [prepayDiscountPct, setPrepayDiscountPct] = useState('');
  const [prepayBankedDiscount, setPrepayBankedDiscount] = useState(0);
  const [discountsTrail, setDiscountsTrail] = useState({ at: null, by: null });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [pRes] = await Promise.all([
          axios.get(`${API}/products`),
        ]);
        if (cancelled) return;
        setProducts(pRes.data);
        const u = user;
        const ov = u.discountOverrides || {};
        // Initialise every discount override from the customer's saved
        // overrides. Missing keys become '' (= no override).
        const initialOverrides = {};
        for (const { key } of DISCOUNT_FIELDS) {
          initialOverrides[key] = ov[key] ?? '';
        }
        setOverrides(initialOverrides);
        // Per-product overrides: numeric values from the server become string inputs.
        const pd = u.productDiscounts || {};
        const pdInput = {};
        for (const [pid, pct] of Object.entries(pd)) pdInput[pid] = String(pct);
        setProductDiscounts(pdInput);
        setHidden(new Set(u.hiddenProductIds || []));
        setSmartSaverEnabled(u.smartSaverEnabled !== false);
        setSingleTubEnabled(u.singleTubEnabled === true);
        setCustomerCode((u.customerCode || '').toUpperCase());
        setBccCustomer(Boolean(u.bccCustomer));
        setPrepayEnabled(u.prepayEnabled !== false);
        setPrepayLabel(
          u.prepayLabel
          || "I'd like to pay before dispatch in exchange for a discount/benefit (we'll be in touch with the offer)"
        );
        setPrepayLabelTrail({
          at: u.prepayLabelUpdatedAt || null,
          by: u.prepayLabelUpdatedBy || null,
        });
        // Prepay reward config (% off next order's pre-GST when opted-in) +
        // current banked $ credit waiting to be applied. Both come from the user
        // doc; banked is non-editable here — adjust via the Clear button only.
        setPrepayDiscountPct(
          u.prepayDiscountPct === undefined || u.prepayDiscountPct === null
            ? '' : String(u.prepayDiscountPct),
        );
        setPrepayBankedDiscount(Number(u.prepayBankedDiscount || 0));
        setDiscountsTrail({
          at: u.discountsUpdatedAt || null,
          by: u.discountsUpdatedBy || null,
        });
      } catch (e) {
        if (!cancelled) setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const toggleHidden = (id) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const clearBanked = async () => {
    if (!window.confirm(
      `Clear the $${prepayBankedDiscount.toFixed(2)} banked prepay credit for ${user.name}?\n\n` +
      'Use this if the customer ticked the prepay opt-in but the prepayment ' +
      'conditions were never fulfilled. The credit will NOT carry to their next order.',
    )) return;
    setError(null);
    setBusy(true);
    try {
      await axios.post(`${API}/users/${user.id}/clear-banked-discount`);
      setPrepayBankedDiscount(0);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setError(null);
    setBusy(true);
    try {
      const cleanOverrides = {};
      for (const f of DISCOUNT_FIELDS) {
        const v = overrides[f.key];
        if (v !== '' && v !== null && v !== undefined && !Number.isNaN(parseFloat(v))) {
          cleanOverrides[f.key] = parseFloat(v);
        }
      }
      const cleanProductDiscounts = {};
      for (const [pid, raw] of Object.entries(productDiscounts)) {
        if (raw === '' || raw === null || raw === undefined) continue;
        const pct = parseFloat(raw);
        if (!Number.isNaN(pct) && pct >= 0 && pct <= 100) cleanProductDiscounts[pid] = pct;
      }
      await axios.put(`${API}/users/${user.id}`, {
        discountOverrides: cleanOverrides,
        productDiscounts: cleanProductDiscounts,
        hiddenProductIds: Array.from(hidden),
        smartSaverEnabled,
        singleTubEnabled,
        customerCode: customerCode.trim(),
        bccCustomer,
        prepayEnabled,
        prepayLabel: prepayLabel.trim(),
        prepayDiscountPct: prepayDiscountPct === '' ? null : parseFloat(prepayDiscountPct),
      });
      onClose(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  // Group products by range for the visibility section. Show every range we have
  // products in — not just the four Saver ranges — so admins can override pricing /
  // hide visibility for Catalogue items too. Saver ranges render first in their
  // canonical order; anything else (Catalogue, future ranges) appends after.
  const SAVER_RANGE_ORDER = ['Core', 'Goal', 'Performance', 'Essentials'];
  const allRanges = Array.from(new Set(products.map((p) => p.range || 'Other')));
  const orderedRanges = [
    ...SAVER_RANGE_ORDER.filter((r) => allRanges.includes(r)),
    ...allRanges.filter((r) => !SAVER_RANGE_ORDER.includes(r)).sort(),
  ];
  const byRange = orderedRanges.map((r) => ({
    range: r,
    items: products.filter((p) => (p.range || 'Other') === r),
  }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => onClose(false)} data-testid="customer-overrides-modal">
      <div className="bg-surface border border-border-color rounded-xl p-6 max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="text-xl font-bold text-gold">Customer overrides</h3>
            <p className="text-sm text-text-secondary">{user.name} — {user.email}</p>
          </div>
          <button onClick={() => onClose(false)} className="text-text-secondary hover:text-text-primary text-2xl leading-none px-2" aria-label="Close">×</button>
        </div>

        {error && <div className="my-3 px-3 py-2 bg-danger/10 border border-danger/30 text-danger text-sm rounded">{error}</div>}

        <section className="mt-4 px-3 py-3 bg-black/40 border border-border-color rounded-lg">
          <label htmlFor="customerCode" className="text-sm font-semibold text-text-primary block mb-1">
            Inventory Customer Code
          </label>
          <p className="text-xs text-text-secondary mb-2">
            Short code used in the inventory-system CSV export (auto-uppercased). Must match the code
            this customer uses in your inventory system. Leave blank if the customer doesn&apos;t
            transact through that system — the inventory file will be skipped for their orders.
          </p>
          <input
            type="text"
            id="customerCode"
            value={customerCode}
            onChange={(e) => setCustomerCode(e.target.value.toUpperCase())}
            data-testid="customer-code-input"
            placeholder="e.g. RAISEY01"
            maxLength={32}
            className="w-full sm:w-64 px-3 py-1.5 bg-black border border-border-color rounded text-text-primary text-sm font-mono focus:border-gold focus:outline-none uppercase"
          />
        </section>

        <section className="mt-4 px-3 py-3 bg-black/40 border border-border-color rounded-lg flex items-start gap-3">
          <input
            type="checkbox"
            id="smartSaverEnabled"
            checked={smartSaverEnabled}
            onChange={(e) => setSmartSaverEnabled(e.target.checked)}
            data-testid="smart-saver-enabled-toggle"
            className="mt-1 w-4 h-4 accent-yellow-500"
          />
          <label htmlFor="smartSaverEnabled" className="cursor-pointer select-none flex-1">
            <span className="text-sm font-semibold text-text-primary">Enable Smart Saver builder for this customer</span>
            <p className="text-xs text-text-secondary mt-0.5">
              When unticked, this customer&apos;s sidebar hides the Smart Saver menu item and the
              builder API rejects saves for them. They can still use Browse &amp; Order normally.
            </p>
          </label>
        </section>

        <section className="mt-4 px-3 py-3 bg-black/40 border border-border-color rounded-lg flex items-start gap-3">
          <input
            type="checkbox"
            id="singleTubEnabled"
            checked={singleTubEnabled}
            onChange={(e) => setSingleTubEnabled(e.target.checked)}
            data-testid="single-tub-enabled-toggle"
            className="mt-1 w-4 h-4 accent-yellow-500"
          />
          <label htmlFor="singleTubEnabled" className="cursor-pointer select-none flex-1">
            <span className="text-sm font-semibold text-text-primary">Enable Single Tub tier for this customer</span>
            <p className="text-xs text-text-secondary mt-0.5">
              Off by default. When unticked, the Single Tub option is hidden on this customer&apos;s
              Smart Saver builder (only Box and Pallet Layer show) and the API blocks new
              Single Tub saves. Existing single-tub savers stay viewable.
            </p>
          </label>
        </section>

        <section className="mt-4 px-3 py-3 bg-black/40 border border-border-color rounded-lg space-y-3" data-testid="customer-order-prefs-section">
          <h4 className="text-sm font-semibold text-text-primary">Order email preferences</h4>

          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={bccCustomer}
              onChange={(e) => setBccCustomer(e.target.checked)}
              data-testid="customer-bcc-toggle"
              className="mt-1 w-4 h-4 accent-yellow-500"
            />
            <span className="flex-1">
              <span className="text-sm font-medium text-text-primary">Send a copy to the customer (BCC)</span>
              <span className="block text-xs text-text-secondary mt-0.5">
                When enabled, this customer is BCC&apos;d on their own submitted-order email so they
                receive a copy of the HTML invoice + CSV attachment.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={prepayEnabled}
              onChange={(e) => setPrepayEnabled(e.target.checked)}
              data-testid="customer-prepay-toggle"
              className="mt-1 w-4 h-4 accent-yellow-500"
            />
            <span className="flex-1">
              <span className="text-sm font-medium text-text-primary">Show prepayment opt-in checkbox</span>
              <span className="block text-xs text-text-secondary mt-0.5">
                Adds a 2nd checkbox to this customer&apos;s submit dialog so they can opt into
                prepayment terms in exchange for a discount/benefit.
              </span>
            </span>
          </label>

          <div>
            <label className="block text-xs text-text-secondary mb-1">
              Prepay checkbox label shown to this customer:
            </label>
            <textarea
              value={prepayLabel}
              onChange={(e) => setPrepayLabel(e.target.value)}
              disabled={!prepayEnabled}
              data-testid="customer-prepay-label-input"
              placeholder="I'd like to pay before dispatch in exchange for a discount/benefit (we'll be in touch with the offer)"
              rows={2}
              maxLength={300}
              className="w-full px-3 py-2 bg-black border border-border-color rounded text-text-primary text-sm focus:border-gold focus:outline-none disabled:opacity-50"
            />
            <p className="text-[10px] text-text-secondary mt-1">
              Tailor this per-customer (e.g.{' '}
              <em>&quot;5% off if paid within 3 days of invoice&quot;</em>) — defaults to the generic copy if blank.
            </p>
            {prepayLabelTrail.at && (
              <p
                className="text-[10px] text-text-secondary mt-1 italic"
                data-testid="customer-prepay-label-trail"
                title={formatNzDateTime(prepayLabelTrail.at)}
              >
                Last edited{prepayLabelTrail.by ? ` by ${prepayLabelTrail.by}` : ''}{' '}
                on {formatNzDate(prepayLabelTrail.at)}
              </p>
            )}
          </div>

          <div className="pt-3 border-t border-border-color/50">
            <label htmlFor="prepayDiscountPct" className="block text-sm font-medium text-text-primary mb-1">
              Prepay reward — % off next order&apos;s pre-GST total
            </label>
            <p className="text-xs text-text-secondary mb-2">
              When this customer ticks the prepay opt-in on a submitted order, this percent
              is banked as a $ credit (calculated against this order&apos;s pre-GST total) and
              auto-applied to their next order. Leftover credit carries forward indefinitely.
              Leave blank or set to 0 to disable the reward (the opt-in checkbox is still shown
              by the toggle above; ticking it just flags the order as prepay-requested).
            </p>
            <div className="flex items-center gap-2">
              <input
                type="number"
                id="prepayDiscountPct"
                step="0.5"
                min="0"
                max="100"
                value={prepayDiscountPct}
                onChange={(e) => setPrepayDiscountPct(e.target.value)}
                disabled={!prepayEnabled}
                data-testid="customer-prepay-pct-input"
                placeholder="0"
                className="w-24 px-2 py-1.5 bg-black border border-border-color rounded text-text-primary text-sm focus:border-gold focus:outline-none text-right disabled:opacity-50"
              />
              <span className="text-text-secondary text-sm">%</span>
            </div>
          </div>

          <div className="pt-3 border-t border-border-color/50">
            <p className="text-sm font-medium text-text-primary mb-1">Banked prepay credit</p>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p
                  className="text-2xl font-bold font-mono text-gold"
                  data-testid="customer-prepay-banked-amount"
                >
                  ${prepayBankedDiscount.toFixed(2)}
                </p>
                <p className="text-[10px] text-text-secondary mt-0.5">
                  {prepayBankedDiscount > 0
                    ? 'Will auto-apply to this customer\'s next submitted order.'
                    : 'No credit banked. A credit will accrue when the customer ticks prepay on submit.'}
                </p>
              </div>
              {prepayBankedDiscount > 0 && (
                <button
                  type="button"
                  onClick={clearBanked}
                  disabled={busy}
                  data-testid="customer-prepay-clear-banked"
                  className="px-3 py-1.5 border border-danger/40 text-danger text-xs rounded hover:bg-danger/10 disabled:opacity-50"
                >
                  Clear banked credit
                </button>
              )}
            </div>
          </div>
        </section>

        <section className="mt-6">
          <h4 className="text-sm font-semibold text-text-primary mb-2">Discount overrides (%)</h4>
          <p className="text-xs text-text-secondary mb-3">Leave blank to use the global default for that bundle type.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {DISCOUNT_FIELDS.map((f) => (
              <div key={f.key}>
                <label className="text-xs text-text-secondary mb-1 block">{f.label}</label>
                <input
                  type="number" step="0.5" min="0" max="100"
                  value={overrides[f.key]}
                  onChange={(e) => setOverrides({ ...overrides, [f.key]: e.target.value })}
                  data-testid={`override-${f.key}`}
                  placeholder="default"
                  className="w-full px-2 py-1.5 bg-black border border-border-color rounded text-text-primary text-sm focus:border-gold focus:outline-none"
                />
              </div>
            ))}
          </div>
          {discountsTrail.at && (
            <p
              className="text-[10px] text-text-secondary mt-2 italic"
              data-testid="customer-discounts-trail"
              title={formatNzDateTime(discountsTrail.at)}
            >
              Discounts last edited{discountsTrail.by ? ` by ${discountsTrail.by}` : ''}{' '}
              on {formatNzDate(discountsTrail.at)}
            </p>
          )}
        </section>

        <section className="mt-6">
          <h4 className="text-sm font-semibold text-text-primary mb-2">Per-product discounts &amp; visibility</h4>
          <p className="text-xs text-text-secondary mb-3">
            Set a <strong>product-specific discount %</strong> off RRP to give this customer a unique wholesale
            unit price (leave blank to use the 25% default off RRP). Tick <strong>Hide</strong> to remove a product
            from this customer&apos;s <em>Smart Saver builder</em> only — the product remains available on
            Browse &amp; Order.
          </p>
          <div className="space-y-3">
            {byRange.map(({ range, items }) => (
              items.length === 0 ? null : (
                <div key={range}>
                  <p className="text-xs text-gold mb-1">{range}</p>
                  <div className="space-y-1">
                    {items.map((p) => (
                      <div
                        key={p.id}
                        className="grid grid-cols-[1fr_auto_auto] items-center gap-3 px-2 py-1.5 bg-black/40 rounded text-xs text-text-primary"
                      >
                        <span className="truncate min-w-0">{p.name}{p.size ? ` (${p.size})` : ''}</span>
                        <div className="flex items-center gap-1.5">
                          <input
                            type="number"
                            step="0.5" min="0" max="100"
                            value={productDiscounts[p.id] ?? ''}
                            onChange={(e) => setProductDiscounts({ ...productDiscounts, [p.id]: e.target.value })}
                            data-testid={`product-discount-${p.id}`}
                            placeholder="—"
                            className="w-16 px-1.5 py-1 bg-black border border-border-color rounded text-text-primary text-xs focus:border-gold focus:outline-none text-right"
                            aria-label={`${p.name} discount percent`}
                          />
                          <span className="text-text-secondary">%</span>
                        </div>
                        <label className="flex items-center gap-1.5 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={hidden.has(p.id)}
                            onChange={() => toggleHidden(p.id)}
                            data-testid={`hide-product-${p.id}`}
                            className="w-3.5 h-3.5 accent-yellow-500"
                          />
                          <span className="text-text-secondary text-[11px]">Hide</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )
            ))}
          </div>
        </section>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={() => onClose(false)} className="px-4 py-2 border border-border-color text-text-primary rounded-lg">Cancel</button>
          <button
            onClick={save}
            disabled={busy}
            data-testid="save-overrides-button"
            className="px-4 py-2 bg-gold text-black font-bold rounded-lg disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Save overrides'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerOverridesModal;
